import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box, Column, Button, ConfirmPopup, CustomSpinner } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import {
  CandidateSortableField,
  CandidateStatus,
  ICandidateListViewEntity,
  useGetCandidateListInfinite,
} from '~/domain/PermanentJobs';
import { CandidateListItem } from './CandidateListItem';

interface ISelectCandidatesPopupProps {
  jobPostingId: string;
  onClose: () => void;
  onConfirm: (candidateIds: string[]) => void;
  defaultSelectedCandidateIds?: string[];
}

export const SelectCandidatesPopup = ({
  jobPostingId,
  onClose,
  onConfirm,
  defaultSelectedCandidateIds = [],
}: ISelectCandidatesPopupProps) => {
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>(
    defaultSelectedCandidateIds,
  );

  // eligible candidates = status: hired + interviewed
  const {
    data: candidates,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useGetCandidateListInfinite(
    {
      jobPostingId,
      status: [CandidateStatus.Hired, CandidateStatus.Interviewed],
      orderBy: CandidateSortableField.Hired,
    },
    { enabled: !!jobPostingId },
  );

  // Infinite scroll trigger
  const { ref: loadMoreRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Auto-select hired candidates on data load
  useEffect(() => {
    const hiredIds = candidates.filter((c) => c.status === CandidateStatus.Hired).map((c) => c.id);

    // Only update if there are new hired candidates not already selected
    const newHiredIds = hiredIds.filter((id) => !selectedCandidates.includes(id));

    if (newHiredIds.length > 0) {
      setSelectedCandidates((prev) => [...new Set([...prev, ...newHiredIds])]);
    }
  }, [candidates, selectedCandidates]);

  const handleToggleCandidate = (candidate: ICandidateListViewEntity) => {
    // Prevent toggling hired candidates - they must remain selected
    if (candidate.status === CandidateStatus.Hired) {
      return;
    }

    setSelectedCandidates((prev) =>
      prev.includes(candidate.id)
        ? prev.filter((id) => id !== candidate.id)
        : [...prev, candidate.id],
    );
  };

  const hasAnyHiredCandidates = candidates.some((c) => c.status === CandidateStatus.Hired);

  return (
    // @ts-expect-error -- ConfirmPopup still written in JavaScript
    <ConfirmPopup
      title="Before You Close This Job"
      description="If you hired through GoTu, select your new team member(s) below. Otherwise, simply close this job to finish."
      descriptionStyle={{ marginBottom: 22 }}
      closePopup={onClose}
      dismissiblePopup={false}
      showClosePopupIcon
    >
      <Box
        width={'80%'}
        overflowY="auto"
        marginBottom={22}
        paddingInline={22}
        style={{
          maxHeight: 248,
          scrollbarWidth: 'thin',
        }}
      >
        {isLoading && candidates.length === 0 && <CustomSpinner isLoading size={32} />}

        {candidates.map((candidate) => (
          <CandidateListItem
            key={candidate.id}
            id={candidate.id}
            name={candidate.professional.name}
            isSelected={selectedCandidates.includes(candidate.id)}
            disabled={candidate.status === CandidateStatus.Hired}
            onToggle={() => handleToggleCandidate(candidate)}
          />
        ))}

        {/* Infinite scroll trigger */}
        {hasNextPage && (
          <div ref={loadMoreRef} style={{ padding: 8, textAlign: 'center' }}>
            {isFetchingNextPage && <CustomSpinner isLoading size={32} />}
          </div>
        )}
      </Box>
      <Column>
        <Button
          text={`Confirm ${selectedCandidates.length || ''} Hires & Close Job`}
          onClick={() => onConfirm(selectedCandidates)}
          disabled={selectedCandidates.length === 0}
          style={{ width: 272, height: 48 }}
        />
        {!hasAnyHiredCandidates && (
          <Button
            text="Close Job"
            onClick={() => onConfirm([])}
            style={{ width: 272, height: 48, marginTop: 16, backgroundColor: Colors.secondary_500 }}
          />
        )}
      </Column>
    </ConfirmPopup>
  );
};
